import { Injectable, computed, signal } from '@angular/core';
import { DEFAULT_CAMERA_VIEW, canZoomIn, canZoomOut, isFitted } from '@render/camera';
import type { CameraView } from '@render/camera';
import type { PixiGameApp } from '@render/pixi-app';
import type { GridPos, GridRect } from '@sim/core/types';

@Injectable({ providedIn: 'root' })
export class GameViewportService {
  private app: PixiGameApp | null = null;
  /** True once the Pixi canvas has mounted and registered — grid-anchored UI can't position before this. */
  readonly ready = signal(false);

  private readonly viewSignal = signal<CameraView>(DEFAULT_CAMERA_VIEW);
  readonly zoom = computed(() => this.viewSignal().camera.zoom);
  readonly isFitted = computed(() => isFitted(this.viewSignal()));
  readonly canZoomIn = computed(() => canZoomIn(this.viewSignal()));
  readonly canZoomOut = computed(() => canZoomOut(this.viewSignal()));

  register(app: PixiGameApp): void {
    this.app = app;
    app.onCameraChange = (view) => this.viewSignal.set(view);
    this.ready.set(true);
  }

  clear(): void {
    this.app = null;
    this.viewSignal.set(DEFAULT_CAMERA_VIEW);
    this.ready.set(false);
  }

  /**
   * Reads the camera before delegating so that every grid-anchored placement built on top of this —
   * the arg pickers, the coach-mark spotlight — re-runs when the map is zoomed or panned. Without
   * it those overlays would stay pinned to stale screen coordinates while the world slid underneath.
   */
  gridCellRect(pos: GridPos): DOMRect | null {
    this.viewSignal();
    return this.app?.gridCellRect(pos) ?? null;
  }

  gridRect(rect: GridRect): DOMRect | null {
    const topLeft = this.gridCellRect(rect.min);
    const bottomRight = this.gridCellRect(rect.max);
    if (!topLeft || !bottomRight) return null;
    return new DOMRect(
      topLeft.left,
      topLeft.top,
      bottomRight.right - topLeft.left,
      bottomRight.bottom - topLeft.top,
    );
  }

  cellAtClientPoint(clientX: number, clientY: number): GridPos | null {
    return this.app?.cellAtClientPoint(clientX, clientY) ?? null;
  }

  zoomIn(): void {
    this.app?.zoomIn();
  }

  zoomOut(): void {
    this.app?.zoomOut();
  }

  resetView(): void {
    this.app?.resetView();
  }
}
